import { ref, computed } from 'vue';

/**
 * useSeatSelection
 *
 * Composable for managing seat selection state in a ticketing/front context.
 * Agnostic of invitations or any specific business flow.
 *
 * Seat object shape (expected from the consumer):
 *   {
 *     id:           string | number   — unique seat identifier
 *     row:          string            — row label (e.g. "A", "B")
 *     number:       string | number   — seat number within the row
 *     available:    boolean           — whether the seat can be selected
 *     label?:       string            — optional display override (e.g. "A-12")
 *     sectionName?: string            — optional zone/section name for display
 *     reducedVisibility?: boolean     — optional accessibility flag
 *     visibilityPercent?: number      — optional reduced-visibility percentage
 *   }
 *
 * ─── Adjacency support ───────────────────────────────────────────────────────
 *
 * By default adjacency enforcement is DISABLED — existing behaviour is unchanged.
 *
 * To activate it, pass an `adjacencyRules` object that conforms to the interface
 * returned by `useAdjacencyRules`:
 *
 *   import { useAdjacencyRules } from './useAdjacencyRules';
 *
 *   // Create the selection composable first so we can pass its internal
 *   // _selectedIds ref to the rules composable:
 *   const selection = useSeatSelection({ seats, maxSelection: 4 });
 *
 *   // …but useSeatSelection now accepts adjacencyRules directly, so the
 *   // recommended pattern is the factory approach:
 *   const selection = useSeatSelection({
 *       seats,
 *       maxSelection: 4,
 *       adjacencyRules: useAdjacencyRules,   // pass the factory, not the instance
 *   });
 *
 * When adjacencyRules is provided as a FACTORY FUNCTION, useSeatSelection calls
 * it internally with `{ selectedIds: _selectedIds, seats }` so the rules
 * composable gets direct access to the reactive internal state. This avoids
 * exposing `_selectedIds` publicly.
 *
 * Alternatively you can pass a pre-created INSTANCE (object with validateToggle):
 *
 *   const selection = useSeatSelection({
 *       seats,
 *       maxSelection: 4,
 *       adjacencyRules: myCustomRulesInstance,
 *   });
 *
 * ─── Blocked toggle feedback ─────────────────────────────────────────────────
 *
 * When a toggle is blocked by adjacency rules, `toggleSeat()` returns `false`
 * AND `adjacencyError.value` is set to the reason string:
 *   - null               → no error
 *   - 'different_row'    → seat is in a different row than current selection
 *   - 'not_adjacent'     → seat is not adjacent to the current contiguous block
 *   - 'would_break_contiguity' → deselecting would leave a gap in the block
 *
 * The consumer can watch `adjacencyError` to show UI feedback.
 *
 * ─── Simplifications vs legacy ───────────────────────────────────────────────
 *
 *   - No adjacency enforcement by default. The legacy had `forzar_adyacentes`
 *     logic inlined in AsientosLfpComponent. Here it is opt-in via strategy
 *     injection (useAdjacencyRules).
 *   - No network calls. Seat data is provided externally.
 *   - No seat-blocking / reservation calls. The consumer handles API calls.
 *
 * @param {Object}                  options
 * @param {Ref<Array>}              [options.seats]           - Reactive array of seat objects.
 * @param {number}                  [options.maxSelection]    - Max seats selectable (default: 1).
 * @param {Function|Object|null}    [options.adjacencyRules]  - Factory fn or pre-created rules
 *                                                              instance. null = disabled (default).
 * @returns {Object} Public API — see bottom of file.
 */
export function useSeatSelection({ seats = ref([]), maxSelection = 1, adjacencyRules = null } = {}) {
    // ─── State ───────────────────────────────────────────────────────────────
    /** Set of selected seat ids (string-normalised for reliable equality) */
    const _selectedIds = ref(new Set());

    /**
     * Last adjacency error reason string (or null).
     * Updated on every toggleSeat call.
     */
    const _adjacencyError = ref(null);

    // ─── Adjacency rules setup ───────────────────────────────────────────────

    /**
     * Resolved rules instance — either the one passed in (if it's already an
     * object with validateToggle) or the result of calling the factory.
     * null when adjacency is disabled.
     */
    const _rules = (() => {
        if (!adjacencyRules) return null;
        // Factory function pattern: consumer passes `useAdjacencyRules` directly
        if (typeof adjacencyRules === 'function') {
            return adjacencyRules({ selectedIds: _selectedIds, seats });
        }
        // Pre-created instance pattern: consumer passes the result of useAdjacencyRules(...)
        if (typeof adjacencyRules === 'object' && typeof adjacencyRules.validateToggle === 'function') {
            return adjacencyRules;
        }
        if (process.env.NODE_ENV !== 'production') {
            console.warn(
                '[useSeatSelection] adjacencyRules must be a factory function or an object with validateToggle(). Rules disabled.',
            );
        }
        return null;
    })();

    // ─── Derived state ───────────────────────────────────────────────────────

    /** Array of full seat objects that are currently selected, in selection order */
    const selectedSeats = computed(() => {
        const ids = _selectedIds.value;
        const list = seats.value ?? seats;
        // Preserve insertion order via the Set iteration order
        return [...ids]
            .map(id => list.find(s => String(s.id) === id))
            .filter(Boolean);
    });

    /** Number of currently selected seats */
    const selectionCount = computed(() => _selectedIds.value.size);

    /** True when the selection has reached maxSelection */
    const limitReached = computed(() => selectionCount.value >= maxSelection);

    /** True when no seat is selected */
    const isEmpty = computed(() => _selectedIds.value.size === 0);

    /**
     * The last adjacency violation reason, or null if no violation occurred.
     * Only meaningful when adjacencyRules is active.
     *
     * Possible values:
     *   null                       — no violation
     *   'different_row'            — seat is in a different row
     *   'not_adjacent'             — seat is not next to the current block
     *   'would_break_contiguity'   — deselecting would leave a gap
     */
    const adjacencyError = computed(() => _adjacencyError.value);

    // ─── Seat-level helpers ──────────────────────────────────────────────────

    /**
     * Returns true if the given seat id is currently selected.
     * @param {string|number} seatId
     */
    function isSelected(seatId) {
        return _selectedIds.value.has(String(seatId));
    }

    /**
     * Returns true if the seat can currently be toggled:
     *   - must be available
     *   - if not selected: limit must not be reached AND adjacency rules (if active) must pass
     *   - if already selected: deselection is allowed unless adjacency rules block it
     *
     * NOTE: this is a synchronous check for UI purposes. The definitive validation
     * happens inside toggleSeat.
     *
     * @param {{ id, row, number, available }} seat
     */
    function canToggle(seat) {
        if (!seat.available) return false;

        const selected = isSelected(seat.id);

        if (!selected && limitReached.value) return false;

        // Fast-path when no rules are active
        if (!_rules) return true;

        const { allowed } = _rules.validateToggle(seat, selected);
        return allowed;
    }

    // ─── Actions ─────────────────────────────────────────────────────────────

    /**
     * Toggle selection for a seat.
     * - If already selected → attempt deselect (adjacency rules may block).
     * - If available and limit not reached → attempt select (adjacency rules may block).
     * - Otherwise → no-op.
     *
     * @param {{ id, row, number, available }} seat
     * @returns {boolean} Whether the toggle was applied.
     */
    function toggleSeat(seat) {
        if (!seat.available) return false;

        const id = String(seat.id);
        const selected = _selectedIds.value.has(id);

        // Check adjacency rules before mutating state
        if (_rules) {
            const { allowed, reason } = _rules.validateToggle(seat, selected);
            if (!allowed) {
                _adjacencyError.value = reason;
                return false;
            }
        }

        // Clear any previous adjacency error on a successful path
        _adjacencyError.value = null;

        // Must create a new Set to trigger Vue 2.7 reactivity
        const next = new Set(_selectedIds.value);

        if (selected) {
            next.delete(id);
            _selectedIds.value = next;
            return true;
        }

        if (limitReached.value) return false;

        next.add(id);
        _selectedIds.value = next;
        return true;
    }

    /**
     * Programmatically select a seat by id (if available, limit not reached,
     * and adjacency rules pass).
     * No-op if already selected.
     * @param {string|number} seatId
     */
    function selectSeat(seatId) {
        const id = String(seatId);
        if (_selectedIds.value.has(id)) return;
        if (limitReached.value) return;
        const list = seats.value ?? seats;
        const seat = list.find(s => String(s.id) === id);
        if (!seat || !seat.available) return;

        if (_rules) {
            const { allowed, reason } = _rules.validateToggle(seat, false);
            if (!allowed) {
                _adjacencyError.value = reason;
                return;
            }
        }

        _adjacencyError.value = null;
        const next = new Set(_selectedIds.value);
        next.add(id);
        _selectedIds.value = next;
    }

    /**
     * Programmatically deselect a seat by id.
     * No-op if not selected.
     * Adjacency rules may block deselection that would break contiguity.
     * @param {string|number} seatId
     */
    function deselectSeat(seatId) {
        const id = String(seatId);
        if (!_selectedIds.value.has(id)) return;
        const list = seats.value ?? seats;
        const seat = list.find(s => String(s.id) === id);

        if (_rules && seat) {
            const { allowed, reason } = _rules.validateToggle(seat, true);
            if (!allowed) {
                _adjacencyError.value = reason;
                return;
            }
        }

        _adjacencyError.value = null;
        const next = new Set(_selectedIds.value);
        next.delete(id);
        _selectedIds.value = next;
    }

    /** Clear all selected seats. Always allowed, bypasses adjacency rules. */
    function clearSelection() {
        _adjacencyError.value = null;
        _selectedIds.value = new Set();
    }

    // ─── Public API ──────────────────────────────────────────────────────────
    return {
        // ── Reactive state ────────────────────────────────────────────
        /** Full seat objects currently selected (in selection order) */
        selectedSeats,
        /** Number of currently selected seats */
        selectionCount,
        /** True when selection count equals maxSelection */
        limitReached,
        /** True when no seat is selected */
        isEmpty,
        /**
         * Last adjacency rule violation reason. null when no violation or
         * when adjacency rules are disabled.
         */
        adjacencyError,

        // ── Predicates ────────────────────────────────────────────────
        /** Returns true if the given seatId is selected */
        isSelected,
        /** Returns true if the seat can be toggled given current state + rules */
        canToggle,

        // ── Actions ───────────────────────────────────────────────────
        /** Toggle select/deselect for a seat object */
        toggleSeat,
        /** Select a seat by id */
        selectSeat,
        /** Deselect a seat by id */
        deselectSeat,
        /** Clear all selected seats (bypasses adjacency rules) */
        clearSelection,
    };
}
